import { useQuery, useReactiveVar } from "@apollo/client";
import { Box, BoxProps, Divider, Stack, VStack } from "@chakra-ui/react"
import { Field, FieldProps, FormikProps, FormikValues } from "formik"
import { useState } from "react";
import { FormFieldLabel } from "../../../components/formFieldLabel/FormFieldLabel";
import { FormSubSectionHeader } from "../../../components/formSubSectionHeader/FormSubSectionHeader";
import { SelectFieldV2 } from "../../../components/selectField/SelectFieldV2";
import { PublisherDomainType, PublisherEnum } from "../../../graphqlExternal/generated";
import { GET_PUBLISHER_DOMAINS } from "../../../graphqlExternal/operations/operations";
import { userDetailsVar } from "../../../lib/cache";
import { getPartnerName } from "../../../utils/displayGetters";
import { IFormDataClassField } from "../../../utils/formsSchema/FormDataClass"
import { publisherMap } from "../../../utils/publisherMap";

interface Props {
    fields: Record<string, IFormDataClassField>;
    formik: FormikProps<FormikValues>;
    defaultValues: Record<string, any>;
}

export const GamCampaignDomains = ({
    formik,
    fields,
    defaultValues,
    ...boxProps
}: Props & BoxProps) => {
    const currentUser = useReactiveVar(userDetailsVar);

    if (!currentUser?.agency.partner && !currentUser?.isStaff) {
        return <>Error</>;
    }

    const [localPublisher, setLocalPublisher] = useState<PublisherEnum>(
        currentUser?.isStaff ? PublisherEnum.NewsUk : publisherMap(currentUser?.agency.partner)
    );

    const {
        publisher,
        domains
    } = fields;

    const { data, error, loading } = useQuery(GET_PUBLISHER_DOMAINS, {
        context: { clientName: "illumaDbEndpoint", type: "graphql" },
        fetchPolicy: "cache-and-network",
        variables: {
            publisher: localPublisher,
        },
    });

    if (error) {
        return <></>;
    }

    return (
        <Box {...boxProps}>
            <FormSubSectionHeader label={"Domains"} />
            <Divider mt={0} mb={4} />
            <Stack align={"flex-start"} width={"100%"} direction="row">
                <VStack w="100%">
                    {currentUser?.isStaff && (
                        <Field name={"publisher"}>
                            {({ form, field }: FieldProps) => {
                                const onChange = (option: { label: string; value: PublisherEnum }) => {
                                    setLocalPublisher(option.value as PublisherEnum);
                                    form.setFieldValue(field.name, option.value);
                                    form.setFieldValue("domains", []);
                                };

                                const onMenuClose = () => {
                                    form.setFieldTouched(field.name, true, true);
                                };

                                return (
                                    <FormFieldLabel
                                        isAdmin={true}
                                        label={publisher.label}
                                        isRequired={publisher.isRequired}
                                        isInvalid={
                                            !!(
                                            form.errors["publisher"] &&
                                            !!form.touched["publisher"]
                                            )
                                        }
                                        errorMessage={form.errors?.publisher?.toString()}
                                    >
                                    <SelectFieldV2
                                        isMulti={false}
                                        onChange={onChange}
                                        onMenuClose={onMenuClose}
                                        placeholder={publisher.placeholder}
                                        options={
                                            Object.entries(PublisherEnum).map(([, value]) => ({
                                                label: getPartnerName(value),
                                                value: value,
                                              }))
                                        }
                                        value={{
                                            label: getPartnerName(localPublisher),
                                            value: localPublisher
                                        } as any}
                                    />
                                    </FormFieldLabel>
                                );
                            }}
                        </Field>
                    )}
                    <Field name={"domains"}>
                        {({ form, field }: FieldProps) => {

                            const onChange = (option: { label: string; value: string }[]) => {
                                const domains = option.map((domain) => domain.value);
                                form.setFieldValue(field.name, domains);
                            };

                            const onMenuClose = () => {
                                form.setFieldTouched(field.name, true, true);
                            };

                            return (
                                <FormFieldLabel
                                    mt={0}
                                    label={domains.label}
                                    isRequired={domains.isRequired}
                                    isInvalid={
                                        !!(
                                        form.errors["domains"] &&
                                        !!form.touched["domains"]
                                        )
                                    }
                                    errorMessage={form.errors?.domains?.toString()}
                                >
                                    <SelectFieldV2
                                        isMulti={true}
                                        onChange={onChange}
                                        onMenuClose={onMenuClose}
                                        placeholder={domains.placeholder}
                                        options={loading ? [] : data.publisherDomains.map((domain: PublisherDomainType) => ({
                                            label: domain.domain,
                                            value: domain.domain,
                                        }))}
                                        value={formik.values.domains.map((domain: string) => ({
                                            label: domain,
                                            value: domain,
                                        }))}
                                    />
                                </FormFieldLabel>
                            );
                        }}
                    </Field>
                </VStack>
            </Stack>
        </Box>
    )
}
